import { applyTheme } from './theme.ts';
import type { WhitelabelSettings, WhitelabelThemeSettings } from './model.ts';

export interface WhitelabelApplicationOptions {
  /** Whether to use CSS injection for applying themes. Default is false and `body.style` will be used. */
  useCssInjection?: boolean;
  /** Whether to enable verbose logging. Default is false. */
  verbose?: boolean;
}

/** Used for CSS injection management */
let _createdElement: HTMLStyleElement | null = null;

/**
 * Applies whitelabel settings to the application
 */
export function applySettings(settings: WhitelabelSettings, options?: WhitelabelApplicationOptions): void {
  const log = options && options.verbose
    // eslint-disable-next-line no-console
    ? console.log
    : () => { /* no-op */ };

  log('[WHITELABEL] Applying whitelabel settings:', settings);

  if (!settings || !settings.enabled) {
    return;
  }

  const theme = settings.productionTheme;
  const isLightTheme = (theme && theme.isLightTheme) || false;

  applyTheme(isLightTheme, false);

  if (options && options.useCssInjection) {
    _applyByCss(theme);
  } else {
    _applyBySetProperty(theme);
  }
}

function _applyBySetProperty(theme: WhitelabelThemeSettings): void {
  if (!theme) {
    return;
  }

  Object.values(theme).forEach(themeSettings => {
    if (!themeSettings || typeof themeSettings !== 'object') {
      return;
    }

    const {
      '--brand-favicon': brandFavicon,
      ...themeProps
    } = themeSettings as Record<string, string>;

    Object.entries(themeProps).forEach(([key, value]) => {
      window.document.body.style.setProperty(key, value);
    });

    updateFavicon(brandFavicon);
  });
}

function _applyByCss(theme: WhitelabelThemeSettings): void {
  if (!theme) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'whitelabel-style';

  const lines: string[] = [];

  Object.entries(theme).forEach(([themeName, themeSettings]) => {
    if (!themeSettings || typeof themeSettings !== 'object') {
      return;
    }

    const {
      '--brand-favicon': brandFavicon,
      ...themeProps
    } = themeSettings as Record<string, string>;

    lines.push(`/* Theme settings: ${themeName} */`);
    lines.push(`:root {`);

    Object.entries(themeProps).forEach(([key, value]) => {
      lines.push(`  ${key}: ${value};`);
    });

    lines.push('}\n\n');

    updateFavicon(brandFavicon);
  });

  style.innerHTML = lines.join('\n');

  if (_createdElement) {
    _createdElement.remove();
  }
  _createdElement = style;
  document.head.appendChild(style);
}

/**
 * Updates the favicon in Head
 * @param brandFavicon URL or base64 of the favicon
 */
export function updateFavicon(brandFavicon: string | null | undefined): void {
  if (!brandFavicon) {
    return;
  }

  // sanitizing: remove spaces and quotes
  let sanitizedUrl = brandFavicon.replace(/[ '"]/g, '');

  // sanitizing: if it's NOT base64, ensure starts with `/`
  if (!sanitizedUrl.startsWith('data:image/') && !sanitizedUrl.startsWith('/')) {
    sanitizedUrl = '/' + sanitizedUrl;
  }

  const links: HTMLLinkElement[] = Array.from(document.querySelectorAll('link[rel~=\'icon\']'));

  // Remove duplicates
  if (links.length > 1) {
    links.slice(1).forEach(link => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    });
  }

  let theLink = links[0];

  if (!theLink) {
    theLink = document.createElement('link');
    theLink.rel = 'icon';
    document.head.appendChild(theLink);
  }
  theLink.href = sanitizedUrl;
}
